import React from 'react';
import { ToggleGroup } from '@ark-ui/react/toggle-group';
import clsx from 'clsx';
import type { BaseInputProps } from '../../types/inputs';
import styles from './inputs.module.scss';

export interface ToggleOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface ToggleGroupInputProps extends BaseInputProps<string[]> {
  options: ToggleOption[];
  /**
   * Povolit výběr více možností. Default `true` — to odlišuje ToggleGroup od
   * {@link SegmentGroupInput} (jedna volba). Při `false` se chová jako
   * jednovýběrová skupina přepínacích tlačítek.
   */
  multiple?: boolean;
  className?: string;
}

/**
 * Atomická skupina přepínacích tlačítek postavená na Ark UI `ToggleGroup`.
 *
 * RHF drží hodnotu jako pole vybraných hodnot (`string[]`). Skupina nemá jeden
 * fokusovatelný cíl pro `label[htmlFor]` — pojmenování řeší `aria-labelledby`
 * na popisek z `<FormField />` (stejný vzor jako {@link RadioGroupInput}).
 */
export const ToggleGroupInput: React.FC<ToggleGroupInputProps> = ({
  name,
  value,
  onChange,
  onBlur,
  ref,
  disabled,
  hasError,
  labelId,
  describedBy,
  testAttributes,
  options,
  multiple = true,
  className,
}) => (
  <ToggleGroup.Root
    ref={ref}
    value={value ?? []}
    multiple={multiple}
    disabled={disabled}
    onValueChange={(details) => onChange(details.value)}
    onBlur={onBlur}
    className={clsx(styles.toggleGroupRoot, className)}
    data-name={name}
    aria-labelledby={labelId}
    aria-describedby={describedBy}
    aria-invalid={hasError || undefined}
    {...testAttributes}
  >
    {options.map((option) => (
      <ToggleGroup.Item
        key={option.value}
        value={option.value}
        disabled={option.disabled}
        className={styles.toggleGroupItem}
        data-testid={`${testAttributes['data-testid']}-option-${option.value}`}
      >
        {option.label}
      </ToggleGroup.Item>
    ))}
  </ToggleGroup.Root>
);

ToggleGroupInput.displayName = 'ToggleGroupInput';
